import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Section from '../components/common/Section';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBanner from '../components/common/ErrorBanner';
import { getMiniEsgApiBase, buildMiniEsgAuthHeaders } from '../utils/miniEsgApi';
import { useToastStore } from '../store/toastStore';

export type EvidenceStatus = 'pending' | 'validating' | 'approved' | 'rejected';

export type EvidenceRecord = {
  id: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  companyName?: string;
  reportId?: string;
  status: EvidenceStatus;
  validationNotes?: string;
  validatedBy?: string;
  validatedAt?: number;
  createdAt: number;
  updatedAt: number;
};

export function EvidenceInboxRoute() {
  const { t } = useTranslation();
  const { showToast } = useToastStore();
  const [evidence, setEvidence] = useState<EvidenceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [reportId, setReportId] = useState('');

  const fetchEvidence = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      const url = `${getMiniEsgApiBase()}/evidence${status ? `?status=${status}` : ''}`;
      const response = await fetch(url, {
        headers: buildMiniEsgAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch evidence: ${response.statusText}`);
      }
      const data = await response.json();
      setEvidence(data.evidence || []);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch evidence');
      setError(error);
      console.error('Fetch evidence error:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchEvidence();
    // Poll for status updates every 5 seconds
    const interval = setInterval(fetchEvidence, 5000);
    return () => clearInterval(interval);
  }, [fetchEvidence]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast(t('evidenceInbox.fileTooLarge') || 'File size exceeds 10MB limit', 'error');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast(t('evidenceInbox.selectFileFirst') || 'Please select a file first', 'error');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1] || reader.result as string;
        const response = await fetch(`${getMiniEsgApiBase()}/evidence/upload`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...buildMiniEsgAuthHeaders(),
          },
          body: JSON.stringify({
            fileName: selectedFile.name,
            fileData: base64,
            mimeType: selectedFile.type || 'application/octet-stream',
            companyName: companyName || undefined,
            reportId: reportId || undefined,
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `Upload failed: ${response.statusText}`);
        }

        showToast(t('evidenceInbox.uploadSuccess') || 'File uploaded successfully', 'success');
        setSelectedFile(null);
        setCompanyName('');
        setReportId('');
        fetchEvidence();
      };
      reader.readAsDataURL(selectedFile);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Upload failed');
      setError(error);
      showToast(error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleValidate = async (id: string, status: EvidenceStatus, notes?: string) => {
    try {
      const response = await fetch(`${getMiniEsgApiBase()}/evidence/${id}/validate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...buildMiniEsgAuthHeaders(),
        },
        body: JSON.stringify({ status, validationNotes: notes }),
      });
      if (!response.ok) {
        throw new Error(`Validation update failed: ${response.statusText}`);
      }
      showToast(t('evidenceInbox.validationUpdated') || 'Validation status updated', 'success');
      fetchEvidence();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Validation update failed');
      setError(error);
      showToast(error.message, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('evidenceInbox.confirmDelete') || 'Are you sure you want to delete this evidence?')) {
      return;
    }
    try {
      const response = await fetch(`${getMiniEsgApiBase()}/evidence/${id}`, {
        method: 'DELETE',
        headers: buildMiniEsgAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }
      showToast(t('evidenceInbox.deleted') || 'Evidence deleted', 'success');
      fetchEvidence();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Delete failed');
      setError(error);
      showToast(error.message, 'error');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusColor = (status: EvidenceStatus) => {
    switch (status) {
      case 'approved':
        return 'text-green-400 bg-green-500/20 border-green-500/40';
      case 'rejected':
        return 'text-red-400 bg-red-500/20 border-red-500/40';
      case 'validating':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/40';
      default:
        return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/40';
    }
  };

  return (
    <Section title={t('evidenceInbox.title') || 'Evidence Inbox'}>
      <ErrorBanner error={error} onRetry={fetchEvidence} showRetry={!!error} />

      {/* Upload Section */}
      <div className="mb-6 border border-evat-neon/30 rounded-lg p-4 bg-[#0b0b0b]">
        <h3 className="text-lg font-orbitron text-evat-neon mb-4">
          {t('evidenceInbox.uploadTitle') || 'Upload Evidence File'}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-neutral-400 mb-2">
              {t('evidenceInbox.file') || 'File'} <span className="text-red-400">*</span>
            </label>
            <input
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.csv,.json"
              className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none text-sm"
            />
            {selectedFile && (
              <div className="mt-2 text-sm text-neutral-400">
                {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                {t('evidenceInbox.companyName') || 'Company Name'} (optional)
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
                placeholder={t('evidenceInbox.companyNamePlaceholder') || 'e.g., Acme Corp'}
              />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                {t('evidenceInbox.reportId') || 'Report ID'} (optional)
              </label>
              <input
                type="text"
                value={reportId}
                onChange={(e) => setReportId(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
                placeholder={t('evidenceInbox.reportIdPlaceholder') || 'Link to Mini ESG report'}
              />
            </div>
          </div>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded hover:bg-evat-neon/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (t('evidenceInbox.uploading') || 'Uploading...') : (t('evidenceInbox.upload') || 'Upload')}
          </button>
        </div>
      </div>

      {/* Filter and List */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded text-sm font-orbitron ${
              statusFilter === 'all'
                ? 'bg-evat-neon/30 border border-evat-neon text-evat-neon'
                : 'bg-neutral-800 border border-neutral-700 text-neutral-400 hover:bg-neutral-700'
            }`}
          >
            {t('evidenceInbox.all') || 'All'}
          </button>
          {(['pending', 'validating', 'approved', 'rejected'] as EvidenceStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 rounded text-sm font-orbitron capitalize ${
                statusFilter === status
                  ? 'bg-evat-neon/30 border border-evat-neon text-evat-neon'
                  : 'bg-neutral-800 border border-neutral-700 text-neutral-400 hover:bg-neutral-700'
              }`}
            >
              {t(`evidenceInbox.status.${status}`) || status}
            </button>
          ))}
        </div>
        <button
          onClick={fetchEvidence}
          disabled={loading}
          className="px-3 py-1 bg-neutral-800 border border-neutral-700 text-neutral-400 rounded hover:bg-neutral-700 text-sm font-orbitron disabled:opacity-50"
        >
          {t('evidenceInbox.refresh') || 'Refresh'}
        </button>
      </div>

      {loading && <LoadingSpinner />}

      {/* Evidence List */}
      {!loading && evidence.length === 0 && (
        <div className="text-sm text-neutral-500 text-center py-8">
          {t('evidenceInbox.noEvidence') || 'No evidence files found'}
        </div>
      )}

      {!loading && evidence.length > 0 && (
        <div className="space-y-3">
          {evidence.map((item) => (
            <div
              key={item.id}
              className="border border-evat-neon/30 rounded-lg p-4 bg-[#0b0b0b] hover:border-evat-neon/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-base font-orbitron text-evat-neon">{item.fileName}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-orbitron border ${getStatusColor(item.status)}`}>
                      {t(`evidenceInbox.status.${item.status}`) || item.status}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-400 space-y-1">
                    <div>
                      {formatFileSize(item.fileSize)} • {item.mimeType}
                    </div>
                    {item.companyName && (
                      <div>
                        {t('evidenceInbox.companyName') || 'Company'}: {item.companyName}
                      </div>
                    )}
                    {item.reportId && (
                      <div>
                        {t('evidenceInbox.reportId') || 'Report ID'}: {item.reportId}
                      </div>
                    )}
                    <div>
                      {t('evidenceInbox.uploadedBy') || 'Uploaded by'}: {item.uploadedBy} •{' '}
                      {new Date(item.createdAt).toLocaleString()}
                    </div>
                    {item.validationNotes && (
                      <div className="mt-2 text-neutral-300 italic">{item.validationNotes}</div>
                    )}
                    {item.validatedBy && item.validatedAt && (
                      <div className="text-xs text-neutral-500">
                        {t('evidenceInbox.validatedBy') || 'Validated by'}: {item.validatedBy} •{' '}
                        {new Date(item.validatedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {item.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleValidate(item.id, 'approved', 'Manually approved')}
                        className="px-3 py-1 bg-green-500/20 border border-green-500/40 text-green-400 rounded text-xs font-orbitron hover:bg-green-500/30"
                      >
                        {t('evidenceInbox.approve') || 'Approve'}
                      </button>
                      <button
                        onClick={() => handleValidate(item.id, 'rejected', 'Manually rejected')}
                        className="px-3 py-1 bg-red-500/20 border border-red-500/40 text-red-400 rounded text-xs font-orbitron hover:bg-red-500/30"
                      >
                        {t('evidenceInbox.reject') || 'Reject'}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="px-3 py-1 bg-red-500/20 border border-red-500/40 text-red-400 rounded text-xs font-orbitron hover:bg-red-500/30"
                  >
                    {t('evidenceInbox.delete') || 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

